import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { identityApi } from '../../services/api';
import { toast } from 'react-hot-toast';
import { UserContext } from '../../context/UserContext';
import { Button, Input, Label, FormField } from '@redpanda/ui';
import { setCrossDomainCookie } from '@redpanda/utils';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation('translation');
  const userContext = useContext(UserContext);

  const [name, setName] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (userContext?.user) {
      setName(userContext.user.name || '');
    }
  }, [userContext?.user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      await identityApi.patch('/auth/me', { name });
      await userContext?.refreshUser();
      toast.success(t('queue.profile.updateProfileSuccess'));
    } catch (err) {
      toast.error(t('queue.profile.updateProfileFailed'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      toast.error(t('queue.profile.allPasswordFieldsRequired'));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error(t('queue.profile.passwordMismatch'));
      return;
    }
    setIsChangingPassword(true);
    try {
      const { data: changed } = await identityApi.post('/auth/change-password', { oldPassword, newPassword });
      // 改密碼後其他裝置的登入會失效，這台換用回傳的新 token（寫回自己網域的 cookie）
      if (changed?.token) setCrossDomainCookie('auth_token', changed.token, '.redpanda-tech.com');
      toast.success(t('queue.profile.changePasswordSuccess'));
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      if (err.response && err.response.data.message === 'Invalid old password') {
        toast.error(t('queue.profile.invalidOldPassword'));
      } else {
        toast.error(t('queue.profile.changePasswordFailed'));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (userContext?.isLoading) {
    return <div className="container mx-auto px-4 md:px-10 py-6 pb-32 text-center">{t('common.loading')}</div>;
  }

  if (!userContext?.user) {
    return <div className="container mx-auto px-4 md:px-10 py-6 pb-32 text-center text-red-500">{t('queue.profile.loadingProfile')}</div>;
  }

  const { user } = userContext;

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('queue.profile.title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('queue.profile.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Profile Information Form */}
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
          <form onSubmit={handleUpdateProfile}>
            <FormField>
              <Label htmlFor="name">{t('queue.profile.name')}</Label>
              <Input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>
            <FormField>
              <Label htmlFor="email">{t('queue.profile.email')}</Label>
              <Input
                type="email"
                id="email"
                value={user?.email || ''}
                disabled
              />
            </FormField>
            <FormField>
              <Label htmlFor="organization">{t('queue.profile.organization')}</Label>
              <Input
                type="text"
                id="organization"
                value={user?.organization?.name || ''}
                disabled
              />
            </FormField>
            <FormField>
              <Label htmlFor="role">{t('queue.profile.role')}</Label>
              <Input
                type="text"
                id="role"
                value={user?.role === 'SUPER_ADMIN' ? t('queue.profile.roleMap.superAdmin') : user?.role === 'ADMIN' ? t('queue.profile.roleMap.admin') : user?.role === 'USER' ? t('queue.profile.roleMap.user') : user?.role || ''}
                disabled
              />
            </FormField>
            <div className="mb-5"></div>
            <Button
              type="submit"
              disabled={isUpdating}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {isUpdating ? t('queue.profile.updating') : t('queue.profile.updateInfo')}
            </Button>
          </form>
        </div>

        {/* Change Password Form */}
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">{t('queue.profile.changePassword')}</h2>
          <form onSubmit={handleChangePassword}>
            <input
              type="text"
              autoComplete="username"
              value={user?.email || ''}
              style={{ display: 'none' }}
              readOnly
              aria-hidden="true"
            />
            <FormField>
              <Label htmlFor="oldPassword">{t('queue.profile.oldPassword')}</Label>
              <Input
                type="password"
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                autoComplete="current-password"
              />
            </FormField>
            <FormField>
              <Label htmlFor="newPassword">{t('queue.profile.newPassword')}</Label>
              <Input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormField>
            <FormField>
              <Label htmlFor="confirmNewPassword">{t('queue.profile.confirmNewPassword')}</Label>
              <Input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormField>
            <div className="mb-5"></div>
            <Button
              type="submit"
              variant="primary"
              disabled={isChangingPassword}
              className="w-full"
            >
              {isChangingPassword ? t('queue.profile.changing') : t('queue.profile.changePassword')}
            </Button>
          </form>
        </div>
      </div>

    </div>
  );
};

export default ProfilePage;
